import { Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild, afterNextRender, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmComponent, ConfirmDialogData } from '../shared/dialogs/confirm/confirm.component';
import { ApiService } from '../_services/api.service';
import { DialogService } from '../shared/components/dialog/dialog.service';
import { Dictation } from '../_interfaces/dictation';
import { EncounterListItem } from '../_interfaces/encounter-list-item';
import type { EncounterStatus } from '../_interfaces/encounter-status';
import { formatDuration as formatDurationHelper, formatDictationSubjectName } from '../_helpers/utils';
import { getDictationRetention } from '../_helpers/retention';
import { EncounterSkeletonComponent } from '../shared/components/encounter-skeleton/encounter-skeleton.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { LoadingVisibilityController } from '../shared/utils/loading-visibility';
import { AuthService } from '../_services/auth.service';
import { AnalyticsService, toAnalyticsEncounterType } from '../_services/analytics.service';
import { calendarDayOffset } from '../_helpers/dmy-date';

const PAGE_SIZE = 20;

interface EncounterGroup {
  key: string;
  label: string;
  items: EncounterListItem[];
}

@Component({
  selector: 'app-encounters',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    EncounterSkeletonComponent,
    TranslateModule
  ],
  templateUrl: './encounters.component.html',
  styleUrl: './encounters.component.scss',
  host: {
    class: 'block h-full p-4 md:px-10 md:pt-8 md:pb-14'
  }
})
export class EncountersComponent implements OnInit, OnDestroy {
  private static readonly DEFAULT_COLUMNS: string[] = [
    'status', 'day', 'time', 'patientName', 'patientId',
    'dateOfBirth', 'n-of-documents', 'duration', 'actions'
  ];

  displayedColumns: string[] = [...EncountersComponent.DEFAULT_COLUMNS];
  dataSource = new MatTableDataSource<EncounterListItem>([]);

  isLoading = false;
  isLoadingMore = false;
  showLoadingSkeleton = false;
  showLoadingMoreSkeleton = false;
  hasMore = true;
  private currentOffset = 0;

  @ViewChild('tableWrapper') tableWrapper?: ElementRef<HTMLDivElement>;
  @ViewChild('mobileListWrapper') mobileListWrapper?: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();
  private readonly injector = inject(Injector);
  private readonly analytics = inject(AnalyticsService);
  private readonly loadingVisibility = new LoadingVisibilityController(visible => {
    this.showLoadingSkeleton = visible;
  });
  private readonly loadingMoreVisibility = new LoadingVisibilityController(visible => {
    this.showLoadingMoreSkeleton = visible;
  });

  constructor(
    private apiService: ApiService,
    private router: Router,
    private dialog: DialogService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    const user = this.authService.getUser();
    const isLegalOrg = user?.entity?.settings?.category === 'Legal';
    this.displayedColumns = isLegalOrg
      ? EncountersComponent.DEFAULT_COLUMNS.filter(c => c !== 'dateOfBirth')
      : [...EncountersComponent.DEFAULT_COLUMNS];

    this.loadInitial();
  }

  ngOnDestroy(): void {
    this.loadingVisibility.destroy();
    this.loadingMoreVisibility.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadInitial(): Promise<void> {
    this.isLoading = true;
    this.loadingVisibility.start();
    this.currentOffset = 0;
    this.hasMore = true;
    this.dataSource.data = [];

    try {
      const items = await this.apiService.getDictations(PAGE_SIZE, 0);
      this.dataSource.data = items.map(d => mapDictationToEncounter(d, this.translate));
      this.currentOffset = items.length;
      this.hasMore = items.length === PAGE_SIZE;
    } catch {
      this.snackBar.open(
        this.translate.instant('Unable to load encounters.'),
        this.translate.instant('Close'),
        { duration: 2000, horizontalPosition: 'right', verticalPosition: 'bottom' }
      );
    } finally {
      this.isLoading = false;
      this.loadingVisibility.stop();
    }
  }

  private async loadMore(): Promise<void> {
    if (this.isLoadingMore || !this.hasMore) return;
    this.isLoadingMore = true;
    this.loadingMoreVisibility.start();
    this.scrollTableToBottom();

    try {
      const items = await this.apiService.getDictations(PAGE_SIZE, this.currentOffset);
      const newEncounters = items.map(d => mapDictationToEncounter(d, this.translate));
      this.dataSource.data = [...this.dataSource.data, ...newEncounters];
      this.currentOffset += items.length;
      this.hasMore = items.length === PAGE_SIZE;
    } catch {
      this.snackBar.open(
        this.translate.instant('Unable to load more encounters.'),
        this.translate.instant('Close'),
        { duration: 2000, horizontalPosition: 'right', verticalPosition: 'bottom' }
      );
    } finally {
      this.isLoadingMore = false;
      this.loadingMoreVisibility.stop();
    }
  }

  get groupedEncounters(): EncounterGroup[] {
    const groups: EncounterGroup[] = [];
    const groupsByKey = new Map<string, EncounterGroup>();

    for (const encounter of this.dataSource.data) {
      const date = encounter.encounterDate;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      let group = groupsByKey.get(key);

      if (!group) {
        group = { key, label: this.formatDay(date), items: [] };
        groupsByKey.set(key, group);
        groups.push(group);
      }

      group.items.push(encounter);
    }

    return groups;
  }

  onTableScroll(): void {
    this.loadMoreWhenNearBottom(this.tableWrapper?.nativeElement);
  }

  onMobileListScroll(): void {
    this.loadMoreWhenNearBottom(this.mobileListWrapper?.nativeElement);
  }

  private loadMoreWhenNearBottom(el?: HTMLElement): void {
    if (!el) return;
    const threshold = 200;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.loadMore();
    }
  }

  private scrollTableToBottom(): void {
    afterNextRender(() => {
      const wrappers = [
        this.tableWrapper?.nativeElement,
        this.mobileListWrapper?.nativeElement,
      ];
      for (const el of wrappers) {
        if (el && el.clientHeight > 0) {
          el.scrollTop = el.scrollHeight;
        }
      }
    }, { injector: this.injector });
  }

  onRowClick(encounter: EncounterListItem): void {
    const id = encounter.originalData._id;
    this.analytics.track('encounter_opened',
      { encounter_id: id,
        day_offset: calendarDayOffset(encounter.encounterDate) })
    this.router.navigate(['/document', id], {
      state: { fromEncounters: true }
    });
  }

  formatDuration = (ms: number): string => formatDurationHelper(ms);

  formatDay(date: Date): string {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (target.getTime() === today.getTime()) return this.translate.instant('Today');
    if (target.getTime() === yesterday.getTime()) return this.translate.instant('Yesterday');

    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  formatTime(date: Date): string {
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  statusLabel(status: EncounterStatus): string {
    const labels: Record<EncounterStatus, string> = {
      'draft': 'Draft',
      'in_progress': 'In progress',
      'done': 'Approved'
    };
    return this.translate.instant(labels[status]);
  }

  /** Delete is only offered for encounters that are not yet approved. */
  canDelete(status: EncounterStatus): boolean {
    return status !== 'done';
  }

  async onDelete(encounter: EncounterListItem, event?: MouseEvent): Promise<void> {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const data: ConfirmDialogData = {
      title: this.translate.instant('Delete encounter?'),
      warning: this.translate.instant('Deleting this encounter and all associated documents cannot be undone, are you sure you wish to proceed?'),
      cancelText: this.translate.instant('Cancel'),
      confirmText: this.translate.instant('Delete encounter')
    };
    const dialogRef = this.dialog.open(ConfirmComponent, { data, presentation: 'dialog' });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        const id = encounter.originalData._id;
        this.apiService.deleteDictation(id).then(() => {
          this.analytics.track('encounter_deleted', {
            encounter_id: id,
            encounter_type: toAnalyticsEncounterType(encounter.originalData.copilot?.type),
            surface: 'encounters_list',
          });
          this.snackBar.open(
            this.translate.instant('Encounter has been deleted'),
            this.translate.instant('Close'),
            { duration: 2000, horizontalPosition: 'right', verticalPosition: 'bottom' }
          );
          this.loadInitial();
        }).catch(() => undefined);
      }
    });
  }
}


function formatDOB(dob: string): Date | null {
  const isOldFormat = /^\d{2}\/\d{2}\/\d{4}$/.test(dob); // dd/mm/yyyy
  if (isOldFormat) {
    const [day, month, year] = dob.split('/');
    dob = `${year}-${month}-${day}`;
  }
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dob); // yyyy-mm-dd date-only ISO
  if (isDateOnly) {
    dob = `${dob}T00:00:00Z`;
  }
  const date = new Date(dob);
  if (isNaN(date.getTime())) return null;
  return date;
}

function mapDictationToEncounter(d: Dictation, translate: TranslateService): EncounterListItem {
  const formattedName = formatDictationSubjectName(d.copilot);
  return {
    status: d.copilot?.status || 'draft',
    patientName: formattedName || translate.instant('No name'),
    patientId: d.copilot?.subject_id || null,
    dateOfBirth: d.copilot?.subject_dob ? formatDOB(d.copilot.subject_dob) : null,
    duration: getDictationRetention(d).showsDuration ? d.duration ?? 0 : null,
    encounterDate: new Date(d.createdAt),
    originalData: d,
    nOfDocuments: d.copilot?.metrics?.totalProcessed || 0
  };
}
